import { updateThreadOptimistically } from "@/hooks/useThreads.tsx";
import type { GetThreadResp } from "@/types/thread.ts";
import type {
  MessageThreadEvent,
  NewMessageReq,
  NewMessageResp,
  ThreadEventItem,
} from "@/types/threadEvents.ts";
import { pendingThreadAttachmentsAtom, pendingThreadMessagesAtom } from "@/atoms/messagesAtoms.ts";
import { appendStreamDelta, streamReplyBuffersAtom } from "@/shared/streamReplyBuffer.ts";
import { messageQueries } from "@/shared/messageQueries.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { isCancel } from "axios";
import { useAtomValue, useSetAtom } from "jotai";
import { selectAtom } from "jotai/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  clearPendingAttachments,
  seedPendingAttachments,
  uploadThenPostMessage,
} from "../Omnibox/attachmentSend.ts";
import type { AttachedFile } from "../Omnibox/types.ts";
import { useThreadSocket } from "./useThreadSocket.ts";

/**
 * Build the optimistic placeholder for a user message. Shared by the in-thread
 * send (below) and the home-page "create thread + send" flow.
 *
 * `status`/`suppressQueuedPill`: when the agent is already running the message
 * genuinely queues (render it as a pill); when idle it's processed immediately
 * (render straight into the feed). A brand-new thread is always idle.
 *
 * `id`: in-thread sends create the placeholder before the POST resolves, so
 * they pass nothing and get a random client id reconciled later via the
 * `tempClientId` socket event. The home-page flow awaits the POST first and
 * passes the real thread event id, so the placeholder dedups directly against
 * the message-list fetch (the reconciliation socket event is unobservable
 * there — it fires before the new page subscribes).
 */
export const buildOptimisticUserMessage = (
  threadId: number,
  body: NewMessageReq,
  {
    status,
    suppressQueuedPill,
    id = Math.random(),
  }: { status: "queued" | "completed"; suppressQueuedPill: boolean; id?: number },
): { item: MessageThreadEvent; clientMessageId: number } => {
  const now = new Date().toISOString();
  const item: MessageThreadEvent = {
    id,
    type: "message",
    turnId: null,
    parentTurnId: null,
    sortDate: now,
    committedAt: null,
    clientOnly: {
      receivedInSession: true,
      clientChatId: id,
      suppressQueuedPill,
    },
    message: {
      id, // Temporary message id; the real message data arrives via the list fetch / socket updates
      type: body.type,
      status,
      content: body.text,
      isRead: true,
      threadId,
      shouldDisplayContent: true,
      author: {
        type: "user",
        name: "User",
        urn: "urn:bb:user:fake",
        handles: [],
      },
    },
  };
  return { item, clientMessageId: id };
};

/**
 * Hook that allows the message list to update during a session. Handles both sending and
 * receiving new messages with the backend.
 *
 * Manages its own state, as opposed to mutating the infinite scroll message list state.
 * It has different update patterns (for example, fetching updates for the same list item)
 * and trying to directly manipulate the infinite scroll state makes both sides more complex.
 *
 * The lifecycle is as follows:
 * 0. We establish a websocket connection to the backend for all new messages.
 * 1. The user sends a message. We immediately create a *local* optimistic item for their message and send the request.
 *    --> The user immediately sees a user message with their prompt and an assistant message with some placeholder.
 *    --> The server responds with a confirmation that the user's message was received and stored.
 * 2. The socket.io server streams out updates, e.g. changed events.
 *    --> When the complete user message item arrives, we remove the optimistic item.
 *    --> This is true whether the changes are in response to a user message or not. All updates are streamed out.
 *        There is no "complete" response from the server, just a stream of updates to any number of events.
 */
export const useSessionChat = (threadId: number, isEventView = false) => {
  const queryClient = useQueryClient();
  const [sessionEvents, setSessionEvents] = useState<Map<number, ThreadEventItem>>(new Map());

  // Scope the map to one thread. Wouter re-renders the same panel instance when
  // only the `:threadId` param changes, and a destination already in the query
  // cache resolves without a loading branch, so `MessageList` — and this hook's
  // state with it — survives a thread→thread switch. `useMessageList` merges the
  // whole map into the feed with no thread filter (events carry no threadId to
  // filter on), so without this the previous thread's messages render in the next
  // one's feed at their sortDate, and its queued sends in that thread's pill
  // stack. Same instance-reuse hazard `HeaderActions`/`renamingThreadId` handle
  // in ThreadDetailPanel, which is why this is a render-phase reset rather than
  // an effect: it lands before the handoff effect below re-seeds on the new id.
  const [eventsThreadId, setEventsThreadId] = useState(threadId);
  if (eventsThreadId !== threadId) {
    setEventsThreadId(threadId);
    setSessionEvents(new Map());
  }

  // The reset above only covers state that has already settled. A socket update
  // fetches its events by id, so a switch can land mid-flight: the handler still
  // closes over the thread it started on, while `setSessionEvents` writes to the
  // slot the new thread now shares (same surviving instance), re-populating the
  // map the reset just cleared. Cancellation doesn't cover it either —
  // `cancelQueries` there is scoped to the same query key, and the key carries
  // the thread id, so leaving the thread cancels nothing. Hence a ref: the async
  // continuation needs the *live* thread id, and its captured one is stale by
  // construction.
  const currentThreadIdRef = useRef(threadId);
  currentThreadIdRef.current = threadId;

  // Subscribe only to this thread's slice of the handoff map, not the whole
  // record — otherwise every thread's add/remove would re-run the effect (and
  // re-render) in every mounted useSessionChat instance.
  const pendingForThreadAtom = useMemo(
    () => selectAtom(pendingThreadMessagesAtom, (map) => map[threadId]),
    [threadId],
  );
  const pendingForThread = useAtomValue(pendingForThreadAtom);
  const setPendingThreadMessages = useSetAtom(pendingThreadMessagesAtom);

  // Consume an optimistic message handed off from another view (e.g. the
  // home-page OmniBox that created this thread and navigated here). Seeding it
  // into sessionEvents on mount renders the user's message immediately instead
  // of flashing an empty feed while the message list fetches. The placeholder
  // is keyed on the real thread event id, so the list fetch dedups against it
  // and the session copy (carrying suppressQueuedPill) wins. Clears the handoff
  // so a later remount can't re-seed a stale placeholder.
  useEffect(() => {
    if (!pendingForThread) return;
    setSessionEvents((prev) => {
      if (prev.has(pendingForThread.id)) return prev;
      const next = new Map(prev);
      next.set(pendingForThread.id, pendingForThread);
      return next;
    });
    setPendingThreadMessages((prev) => {
      const { [threadId]: _consumed, ...rest } = prev;
      return rest;
    });
  }, [threadId, pendingForThread, setPendingThreadMessages]);

  // Clear the provisional attachment-chip overlay once the real file artifacts
  // arrive: the `summarizeFile` job attaches all of them (succeeded + failed) in
  // one shot and emits a thread_event:update, so the reconciled message (matched
  // by clientChatId) gaining any `file` artifact means summarization is done and
  // the real chips can take over. Keyed on the same tempClientId we minted.
  const pendingAttachmentsForThread = useAtomValue(
    useMemo(() => selectAtom(pendingThreadAttachmentsAtom, (map) => map[threadId]), [threadId]),
  );
  const setPendingThreadAttachments = useSetAtom(pendingThreadAttachmentsAtom);
  useEffect(() => {
    if (!pendingAttachmentsForThread) return;
    const { tempClientId } = pendingAttachmentsForThread;
    let resolved = false;
    for (const item of sessionEvents.values()) {
      if (item.type !== "message") continue;
      if (item.clientOnly?.clientChatId !== tempClientId) continue;
      if (item.message.artifacts?.some((a) => a.artifactType === "file")) {
        resolved = true;
        break;
      }
    }
    if (!resolved) return;
    setPendingThreadAttachments((prev) => {
      if (!(threadId in prev)) return prev;
      const { [threadId]: _resolved, ...rest } = prev;
      return rest;
    });
  }, [threadId, sessionEvents, pendingAttachmentsForThread, setPendingThreadAttachments]);

  /**
   * Insert a local optimistic user message and return its client id plus a
   * `rollback`, mirroring the `sendMutation` optimistic path but for callers that
   * POST to a different endpoint (e.g. the artifact-decisions batch commit). The
   * caller passes the returned id back to the server as `tempClientId` so the
   * real message reconciles against this placeholder (carrying
   * `suppressQueuedPill`). On error, call `rollback` to drop the placeholder and
   * revert the optimistic `running` state.
   */
  const addOptimisticUserMessage = useCallback(
    (body: NewMessageReq): { clientMessageId: number; rollback: () => void } => {
      const cachedThread = queryClient.getQueryData<GetThreadResp>(["threads", "detail", threadId]);
      const isAgentRunning =
        cachedThread?.activityState === "running" || cachedThread?.activityState === "stopPending";
      const { item, clientMessageId } = buildOptimisticUserMessage(threadId, body, {
        status: isAgentRunning ? "queued" : "completed",
        suppressQueuedPill: !isAgentRunning,
      });
      setSessionEvents((prev) => new Map(prev).set(clientMessageId, item));
      updateThreadOptimistically(queryClient, threadId, { activityState: "running" });
      const rollback = () => {
        setSessionEvents((prev) => {
          if (!prev.has(clientMessageId)) return prev;
          const next = new Map(prev);
          next.delete(clientMessageId);
          return next;
        });
        // Only revert if this call is what flipped idle → running; if the agent
        // was already running, leave it running.
        if (!isAgentRunning) {
          updateThreadOptimistically(queryClient, threadId, { activityState: "stopped" });
        }
      };
      return { clientMessageId, rollback };
    },
    [queryClient, threadId],
  );

  /**
   * Remove a session-events entry by its map key. Used by the queued-message
   * cancel flow to drop a pill immediately without waiting for the
   * server-cascade refetch.
   */
  const removeSessionEvent = (eventId: number) => {
    setSessionEvents((prev) => {
      if (!prev.has(eventId)) return prev;
      const next = new Map(prev);
      next.delete(eventId);
      return next;
    });
  };

  const sendMutation = useMutation({
    mutationFn: async (body: NewMessageReq & { attachedFiles?: AttachedFile[] }) => {
      const { attachedFiles, ...messageBody } = body;
      // Attachment send: upload bytes then POST (returns fast — the backend
      // defers summarization to a job). The optimistic message + uploading chips
      // are already on screen from onMutate. `tempClientId` is set on the body
      // there, so reconciliation works the same as a plain text send.
      if (attachedFiles && attachedFiles.length > 0) {
        await uploadThenPostMessage(
          threadId,
          attachedFiles,
          messageBody,
          setPendingThreadAttachments,
        );
        return {} as NewMessageResp;
      }
      const response = await api.post<NewMessageResp>(
        `/api/a/threads/${threadId}/messages`,
        messageBody,
      );
      return response.data;
    },
    onMutate: async (body) => {
      // The server always stores new messages as `queued`. When the agent is
      // already running, the message genuinely sits behind the in-flight run, so
      // mirror that and render it in the QueuedMessageStack above the OmniBox.
      // When the agent is idle, the message is processed immediately — render it
      // straight into the feed (`suppressQueuedPill`) so the user doesn't see a
      // queued pill flash up and vanish. The flag rides through reconciliation so
      // the real `queued` message stays in the feed until the run completes it.
      //
      // Known limitation: the flag is session-only (not persisted). If the user
      // reloads during the ~1s window before the queue loop flips the message to
      // `completed`, the refetched `queued` message has no flag and briefly shows
      // as a pill. Acceptable for now given the tiny window; revisit if reports
      // of post-reload pill flashes surface.
      const cachedThread = queryClient.getQueryData<GetThreadResp>(["threads", "detail", threadId]);
      const isAgentRunning =
        cachedThread?.activityState === "running" || cachedThread?.activityState === "stopPending";

      const { item: localUserMessageItem, clientMessageId } = buildOptimisticUserMessage(
        threadId,
        body,
        { status: isAgentRunning ? "queued" : "completed", suppressQueuedPill: !isAgentRunning },
      );

      // Attach tempClientId so the server can map the real message back to this optimistic one
      body.tempClientId = clientMessageId;

      // Seed the provisional attachment chips (uploading) keyed to this message
      // so they show instantly, before the upload starts in mutationFn.
      if (body.attachedFiles && body.attachedFiles.length > 0) {
        seedPendingAttachments(
          setPendingThreadAttachments,
          threadId,
          clientMessageId,
          body.attachedFiles,
        );
      }

      // Add optimistic message directly using clientMessageId as key
      setSessionEvents((prev) => {
        const newMap = new Map(prev);
        newMap.set(clientMessageId, localUserMessageItem);
        return newMap;
      });

      // mutate the thread state optimistically so it appears as running right away
      updateThreadOptimistically(queryClient, threadId, { activityState: "running" });

      return {
        clientMessageId,
        localUserMessageItem,
      };
    },
    onError: async (_error, _variables, context) => {
      // Remove optimistic message on error
      if (context?.clientMessageId) {
        setSessionEvents((prev) => {
          const newMap = new Map(prev);
          newMap.delete(context.clientMessageId);
          return newMap;
        });
      }
      // Drop the chip overlay too — the composer still holds the text + files for
      // a retry (handleSubmit only clears it on success).
      clearPendingAttachments(setPendingThreadAttachments, threadId);
      updateThreadOptimistically(queryClient, threadId, { activityState: "stopped" });
    },
  });

  const view = isEventView ? ("events" as const) : ("messages" as const);

  /**
   * Fetch event items by IDs and merge into session state. Returns the merged
   * items so the caller can settle only the finalized ones — on a fetch error (or
   * cancellation) nothing is returned, so an in-flight streamed reply keeps its
   * row (and visible text) instead of being settled into a blank.
   *
   * Nothing is merged if the thread changed while the fetch was in flight: these
   * events belong to the thread we left, and the map is now the next one's.
   */
  const fetchAndMergeItems = async (
    eventIds: number[],
    idToClientId?: Record<number, number>,
  ): Promise<ThreadEventItem[]> => {
    const requestThreadId = threadId;
    const fetchQueryOptions = messageQueries.byIds(requestThreadId, eventIds, view);
    try {
      // Cancel in-flight queries so we re-fire for later events instead of ignoring them
      await queryClient.cancelQueries({ queryKey: fetchQueryOptions.queryKey });
      // Fetch the specific items that changed. The socket event only sends IDs, we need the real data.
      const messageList = await queryClient.fetchQuery(fetchQueryOptions);
      if (currentThreadIdRef.current !== requestThreadId) return [];
      const items = messageList.items as ThreadEventItem[];

      setSessionEvents((prev) => {
        const newMap = new Map(prev);

        for (const item of items) {
          let clientOnly: ThreadEventItem["clientOnly"] = {
            receivedInSession: true,
            ...(newMap.get(item.id)?.clientOnly ?? {}),
          };

          // If this event is replacing an optimistic one, migrate its clientOnly state
          // (This should really only apply to type:message)
          const tempClientId = idToClientId?.[item.id];
          if (tempClientId) {
            const optimistic = newMap.get(tempClientId);
            if (optimistic) {
              clientOnly = { ...optimistic.clientOnly, ...clientOnly };
              newMap.delete(tempClientId);
            } else {
              // No local placeholder to migrate: the message was written by the
              // server for an action this client fired (a card decision — see
              // `fireArtifactAction`), or sent from another client of the same
              // account. Either way the id says it's ours, so it gets the same
              // treatment a typed send does.
              //
              // `suppressQueuedPill` unconditionally, where the send path
              // decides it from whether the agent was idle. The server stores
              // every new message `queued`, so without this the bubble spends
              // the ~1s until the queue loop completes it beside the omnibox
              // and then hops into the feed. We can't run the send path's test
              // honestly here — the action itself has usually already flipped
              // the thread to running by now — and showing a message you just
              // caused in the feed reads better than a pill either way.
              clientOnly = { ...clientOnly, clientChatId: tempClientId, suppressQueuedPill: true };
            }
          }

          newMap.set(item.id, { ...item, clientOnly });
        }

        return newMap;
      });

      return items;
    } catch (error) {
      // Ignore cancellation errors - they're expected when newer socket events arrive
      if (isCancel(error)) {
        console.debug("Event fetch cancelled (newer update received)");
      } else {
        console.error("Failed to fetch events by IDs:", error);
      }
      return [];
    }
  };

  // Live reply streaming: accumulate each streamed reply's appended deltas into
  // `streamReplyBuffersAtom`, keyed by the reply's `userResponse` artifact id (the
  // `thread_event:message_stream` frame carries it). The atom is a pure reveal
  // cache the artifact's own `ResponseArtifactItem` reads while it is `inProgress`;
  // finalize/abort arrive as a `status` change on the row, so this only ever
  // appends. The write is side-effect-only — we never read the atom here — so a
  // delta re-renders only the leaf reading that slice, not the feed.
  const setStreamBuffers = useSetAtom(streamReplyBuffersAtom);
  useThreadSocket(
    threadId,
    "thread_event:message_stream",
    ({ artifactId, delta, atomicRanges }) => {
      // A new attempt streams into its own artifact id, so buffers never collide.
      setStreamBuffers((prev) => ({
        ...prev,
        [artifactId]: appendStreamDelta(prev[artifactId], delta, atomicRanges),
      }));
    },
  );

  // Listen for thread event updates via websocket
  useThreadSocket(threadId, "thread_event:update", async (socketEvent) => {
    // Use idsByType to filter which events to fetch based on the current view mode.
    // In messages view, only fetch message-type events; in events view, fetch all types.
    const eventIds = isEventView
      ? Object.values(socketEvent.idsByType).flat()
      : (socketEvent.idsByType.message ?? []);

    console.debug("Thread events changed via websocket:", eventIds);

    if (eventIds.length === 0) return;

    // A streamed reply's finalize and abort both ride this path as a status
    // change; the leaf switches sources off status, so there's nothing to settle.
    await fetchAndMergeItems(eventIds, socketEvent.idToClientId);
  });

  return {
    sessionEvents,
    sendMutation,
    removeSessionEvent,
    addOptimisticUserMessage,
  };
};
